import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../contexts/AuthContext';
import { phaseService } from '../../../services/phaseService';
import { realtimeService } from '../../../services/realtimeService';
import { Sparkles } from 'lucide-react';

const ProjectCard = ({ project, userRole, onDelete }) => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [localPhaseProgress, setLocalPhaseProgress] = React.useState(null);

  const [urgentRemarks, setUrgentRemarks] = React.useState([]);
  const [allRemarks, setAllRemarks] = React.useState([]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'in_progress': case'on-track':
        return 'bg-green-100 text-green-600 border-green-200';
      case 'delayed':
        return 'bg-orange-100 text-orange-600 border-orange-200';
      case 'urgent':
        return 'bg-red-100 text-red-600 border-red-200';
      case 'completed':
        return 'bg-blue-100 text-blue-600 border-blue-200';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'in_progress':
        return 'CheckCircle2';
      case 'delayed':
        return 'AlertCircle';
      case 'urgent':
        return 'AlertTriangle';
      case 'completed':
        return 'CheckCircle';
      default:
        return 'Circle';
    }
  };

  const projectStartDate = project?.estimatedStartDate || project?.startDate;
  const projectEndDate = project?.estimatedEndDate || project?.endDate;
  const projectStatus = project?.projectStatus || project?.status;

  const currentPhase =
    project?.phases?.find(p => p?.progress > 0 && p?.progress < 100) ||
    project?.phases?.find(p => p?.progress === 100) ||
    project?.phases?.[0] ||
    null;

  const phaseProgress =
    localPhaseProgress !== null
      ? localPhaseProgress
      : currentPhase?.progress ?? project?.phaseProgress ?? 0;

  /* ---------------- LOAD REMARKS ---------------- */

  React.useEffect(() => {
    const loadRemarks = async () => {
      if (currentPhase?.id) {
        try {
          const remarks = await phaseService?.getPhaseRemarks(currentPhase?.id);

          setAllRemarks(remarks || []);

          const urgent = remarks?.filter(r => r?.label === 'urgent');
          setUrgentRemarks(urgent || []);
        } catch (error) {
          console.error('Error loading remarks:', error);
        }
      }
    };

    loadRemarks();
  }, [currentPhase?.id]);

  /* ---------------- REALTIME PROGRESS ---------------- */

  React.useEffect(() => {
    if (project?.id && currentPhase?.id) {
      const handlePhaseUpdate = (updatedPhase) => {
        if (updatedPhase?.id === currentPhase?.id) {
          setLocalPhaseProgress(updatedPhase?.progress);
        }
      };

      realtimeService?.subscribeToPhaseUpdates(project?.id, handlePhaseUpdate);

      return () => {
        realtimeService?.unsubscribe(`phase-updates-${project?.id}`);
      };
    }
  }, [project?.id, currentPhase?.id]);

  const handleCardClick = () => {
    navigate('/project-overview', { state: { projectId: project?.id } });
  };

  const handleManagePhases = (e) => {
    e?.stopPropagation();

    if (!currentPhase) return;

    navigate('/phase-management', {
      state: {
        projectId: project?.id,
        projectName: project?.name,
        phase: {
          id: currentPhase?.id,
          name: currentPhase?.phase_name,
          order: currentPhase?.phase_order,
          status: currentPhase?.status,
          progress: currentPhase?.progress || 0,
          suggestedStart: currentPhase?.planned_start_date,
          suggestedEnd: currentPhase?.planned_end_date,
          actualStart: currentPhase?.actual_start_date,
          actualEnd: currentPhase?.actual_end_date,
          requiredTrade: currentPhase?.required_trade
        },
        projectStatus: projectStatus
      }
    });
  };

  const handleDelete = async (e) => {
    e?.stopPropagation();
    if (window.confirm(`Are you sure you want to delete "${project?.name}"?`)) {
      await onDelete?.(project?.id);
    }
  };

  return (
    <div
      onClick={handleCardClick}
      className="bg-card border border-border rounded-lg p-6 hover:shadow-elevated transition-smooth cursor-pointer"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <h3 className="text-lg font-semibold text-foreground">
              {project?.name}
            </h3>
            {project?.source === 'ai_import' && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 border border-violet-200 text-xs font-medium">
                <Sparkles size={11} />
                AI Imported
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="Calendar" size={16} strokeWidth={2} />
            <span>
              {projectStartDate || 'N/A'} - {projectEndDate || 'N/A'}
            </span>
          </div>
        </div>

        <div
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium ${getStatusColor(
            projectStatus
          )}`}
        >
          <Icon name={getStatusIcon(projectStatus)} size={14} strokeWidth={2} />
          <span className="capitalize">
            {projectStatus?.replace('_', ' ')}
          </span>
        </div>
      </div>
      {currentPhase && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground">
              Current Phase
            </span>

            <span className="text-sm text-muted-foreground">
              {phaseProgress}%
            </span>
          </div>

          <div className="w-full bg-muted rounded-full h-2 mb-2">
            <div
              className="bg-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${phaseProgress}%` }}
            />
          </div>

          <div className="flex items-center gap-2">
            <Icon name="Hammer" size={16} strokeWidth={2} className="text-primary" />

            <span className="text-sm font-medium text-foreground">
              {currentPhase?.phase_name}
            </span>
          </div>

          {/* ---------------- REMARK INDICATORS ---------------- */}

          {(allRemarks?.length > 0 || urgentRemarks?.length > 0) && (
            <div className="flex items-center gap-2 mt-2 flex-wrap">

              {allRemarks?.length > 0 && (
                <div className="flex items-center gap-1 text-xs px-2 py-1 rounded bg-muted border border-border">
                  <Icon name="MessageSquare" size={12} />
                  <span>{allRemarks?.length} remark{allRemarks?.length > 1 ? 's' : ''}</span>
                </div>
              )}

              {urgentRemarks?.length > 0 && (
                <div className="flex items-center gap-1 text-xs px-2 py-1 rounded bg-red-100 text-red-700 border border-red-200">
                  <Icon name="AlertTriangle" size={12} />
                  <span>{urgentRemarks?.length} urgent</span>
                </div>
              )}

            </div>
          )}
        </div>
      )}
      <div className="flex items-center gap-2 pt-4 border-t border-border">
        <Button
          variant="outline"
          size="sm"
          iconName="Eye"
          iconPosition="left"
          onClick={handleCardClick}
          className="flex-1"
        >
          View Details
        </Button>

        <Button
          variant="default"
          size="sm"
          iconName="ListChecks"
          iconPosition="left"
          onClick={handleManagePhases}
          className="flex-1"
        >
          Manage Phases
        </Button>

        <Button
          variant="outline"
          size="sm"
          iconName="Trash2"
          onClick={handleDelete}
          className="text-destructive hover:bg-destructive/10"
        />
      </div>
    </div>
  );
};

export default ProjectCard;